import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Info, Loader2, PenLine, Sparkles, Tag, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { updateHouseBrand } from "../../redux/operatorConsoleSlice";
import { isBetHousePartnershipReadyForConsole } from "../../utils/betHousePartners";

const NAME_MAX = 50;
const STORY_MAX = 1000;

const HouseBrandModal = ({ open, onClose, bet, operatorUserId, onSuccess }) => {
  const dispatch = useDispatch();
  const { houseSettingsSaving, houseSettingsError } = useSelector((s) => s.operatorConsole);

  const [name, setName] = useState("");
  const [brandStory, setBrandStory] = useState("");
  const [errors, setErrors] = useState({});

  const partnershipReady = isBetHousePartnershipReadyForConsole(bet);

  useEffect(() => {
    if (open && bet) {
      setName(bet.name || "");
      setBrandStory(bet.brandStory || "");
      setErrors({});
    }
  }, [open, bet]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !houseSettingsSaving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, houseSettingsSaving, onClose]);

  if (!open || !bet) return null;

  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = "House name is required";
    else if (name.trim().length < 3) next.name = "At least 3 characters";
    else if (name.trim().length > NAME_MAX) next.name = `Max ${NAME_MAX} characters`;
    if (brandStory.trim().length > STORY_MAX) next.brandStory = `Max ${STORY_MAX} characters`;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!partnershipReady || !operatorUserId || !validate()) return;

    const nameChanged = name.trim() !== bet.name;
    const brandChanged = brandStory.trim() !== (bet.brandStory || "");
    if (!nameChanged && !brandChanged) {
      setErrors({ form: "Change at least one field before saving." });
      return;
    }

    try {
      await dispatch(
        updateHouseBrand({
          betId: bet._id,
          operatorUserId,
          name: name.trim(),
          brandStory: brandStory.trim(),
        })
      ).unwrap();
      onSuccess?.();
      onClose();
    } catch {
      /* error in redux */
    }
  };

  const formError = errors.form || (houseSettingsError ? String(houseSettingsError) : null);

  return createPortal(
    <div className="oc-treasury-modal-root oc-brand-modal-root" role="dialog" aria-modal="true">
      <button
        type="button"
        className="oc-treasury-modal-backdrop"
        aria-label="Close"
        onClick={() => !houseSettingsSaving && onClose()}
      />
      <form
        className={`oc-settings-modal-sheet ${houseSettingsSaving ? "oc-treasury-modal-sheet-busy" : ""}`}
        onSubmit={handleSubmit}
      >
        {houseSettingsSaving ? (
          <div className="oc-treasury-modal-busy" aria-label="Updating">
            <Loader2 size={24} className="oc-treasury-modal-spin" />
          </div>
        ) : null}

        <header className="oc-settings-modal-head">
          <div className="oc-treasury-modal-head-main">
            <span className="oc-treasury-modal-icon">
              <PenLine size={18} />
            </span>
            <div className="oc-settings-modal-head-copy">
              <p className="oc-treasury-modal-eyebrow">House brand</p>
              <h2 id="oc-brand-modal-title">Update house</h2>
              <p className="oc-treasury-modal-sub">
                Name and brand notes bettors see on your public profile.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="oc-treasury-modal-close"
            onClick={onClose}
            disabled={houseSettingsSaving}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>

        <div className="oc-settings-modal-body">
          {!partnershipReady ? (
            <div className="oc-settings-alert" role="status">
              All partners must accept before you can update branding.
            </div>
          ) : null}

          <div className="oc-settings-field">
            <div className="oc-settings-field-label">
              <Tag size={13} aria-hidden />
              <span>House name</span>
            </div>
            <div className={`oc-settings-input-wrap ${errors.name ? "oc-settings-input-wrap-error" : ""}`}>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, NAME_MAX))}
                disabled={!partnershipReady || houseSettingsSaving}
                placeholder="Your market name"
                maxLength={NAME_MAX}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? "brand-name-error" : "brand-name-hint"}
              />
              <span className="oc-settings-counter tabular-nums">
                {name.length}/{NAME_MAX}
              </span>
            </div>
            {errors.name ? (
              <p id="brand-name-error" className="oc-settings-field-error">
                {errors.name}
              </p>
            ) : (
              <p id="brand-name-hint" className="oc-settings-field-hint">
                Display name shown across BetBond and your house page.
              </p>
            )}
          </div>

          <div className="oc-settings-field">
            <div className="oc-settings-field-label">
              <Sparkles size={13} aria-hidden />
              <span>Brand notes</span>
            </div>
            <div
              className={`oc-settings-input-wrap oc-settings-textarea-wrap ${
                errors.brandStory ? "oc-settings-input-wrap-error" : ""
              }`}
            >
              <textarea
                value={brandStory}
                onChange={(e) => setBrandStory(e.target.value.slice(0, STORY_MAX))}
                disabled={!partnershipReady || houseSettingsSaving}
                placeholder="Share your story, strategy, or what makes this house special…"
                rows={5}
                maxLength={STORY_MAX}
                aria-invalid={Boolean(errors.brandStory)}
                aria-describedby={errors.brandStory ? "brand-story-error" : "brand-story-hint"}
              />
            </div>
            <div className="oc-settings-field-meta">
              {errors.brandStory ? (
                <p id="brand-story-error" className="oc-settings-field-error">
                  {errors.brandStory}
                </p>
              ) : (
                <p id="brand-story-hint" className="oc-settings-field-hint">
                  Optional marketing message for bettors.
                </p>
              )}
              <span className="oc-settings-counter tabular-nums">
                {brandStory.length}/{STORY_MAX}
              </span>
            </div>
          </div>

          <div className="oc-settings-tip">
            <Info size={14} aria-hidden />
            <p>Brand notes appear on your public house profile for all viewers.</p>
          </div>

          {formError ? <p className="oc-settings-form-error">{formError}</p> : null}
        </div>

        <footer className="oc-settings-modal-foot">
          <button
            type="button"
            className="oc-treasury-cancel"
            onClick={onClose}
            disabled={houseSettingsSaving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="oc-treasury-submit"
            disabled={!partnershipReady || houseSettingsSaving}
          >
            Save brand
          </button>
        </footer>
      </form>
    </div>,
    document.body
  );
};

export default HouseBrandModal;
